/**
 * V2 User API Service
 * JWT + Firebase 인증 사용
 */

import { getFirebaseAuth } from '@/config/firebase.config'
import { apiInterceptor } from '@/networks/api/interceptor'

export interface IUserV2 {
  id: string
  email: string
  displayName?: string
  profileImageUrl?: string
  phoneNumber?: string
  locale?: string // 'en-US' | 'ko-KR' | 'ja-JP' | 'zh-CN'
  timezone?: string
  is2FAEnabled?: boolean
  isPasswordResetRequired?: boolean
  currency?: string // V2: 'USD' | 'KRW' | 'JPY' | 'EUR'
  currencyId?: number // V2: metadata.currencyId (읽기 전용)
  createdAt: string
  updatedAt: string
}

export interface ILoginResponseV2 {
  loginStatus: string
  userInfo: IUserV2
  token: {
    access: string
    refresh: string
  }
  walletId: string | null
}

export interface IOAuthProvider {
  provider: string
  clientId: string
  authorizationEndpoint: string
  redirectUrl: {
    wallet: string
    walletV2: string
  }
}

export interface IApp {
  id: string
  name: string
  workspaceId: string
  description?: string
  status: string
  iconImageUrl?: string
  logoImageUrl?: string
  allowedDomains?: string[]
  supportedAssets?: string[]
  themeId?: string
  layoutId?: string
  theme?: {
    id: string
    name: string
    config: any
  }
  layout?: {
    id: string
    name: string
    config: any
  }
  workspace?: {
    id: string
    name: string
  }
  createdAt: string
  updatedAt: string
}

export interface ICheckEmailExistResponse {
  exists: boolean
  isEmailExist: boolean
  isEmailverified: boolean
  providerIds: string[]
}

// 2FA 관련 인터페이스
export interface I2FASetupResponse {
  qrCodeUri: string // V2 API는 qrCodeUri 사용
  qrCodeUrl?: string // 호환성
  secret: string
  recoveryCodes: string[]
  status?: string
}

export interface I2FAStatusResponse {
  enabled: boolean
  use2FA: number
}

export interface I2FAVerifyResponse {
  result: boolean
  use2FA?: number
}

export interface IRecoveryCodesResponse {
  recoveryCodes: { code: string; state: boolean }[]
}

export interface IUserAPIServiceV2 {
  login(): Promise<ILoginResponseV2>
  logout(): Promise<void>
  getUserDetail(): Promise<IUserV2>
  updateUser(updates: Partial<IUserV2>): Promise<IUserV2>
  uploadProfileImage(formData: FormData): Promise<{ profileImageUrl: string }>
  deleteUser(): Promise<void>
  getPubKey(): Promise<{ pubKey: string }>
  checkEmailExist(email: string): Promise<ICheckEmailExistResponse>
  verifyUser(params: {
    type: string
    email: string
    localeId?: number
    promotionId?: string
    campaignId?: string
    referralCode?: string
  }): Promise<any>
  getApps(): Promise<IApp[]>
  getAppsMe(): Promise<IApp[]>
  getOAuthProviders(): Promise<IOAuthProvider[]>
  // 2FA 관련
  setup2FA(): Promise<I2FASetupResponse>
  verify2FA(params: {
    code: string
    isRecoveryCode?: boolean
  }): Promise<I2FAVerifyResponse>
  get2FAStatus(): Promise<I2FAStatusResponse>
  getRecoveryCodes(params: {
    code: string
    isRecoveryCode?: boolean
  }): Promise<IRecoveryCodesResponse>
  regenerateRecoveryCodes(params: {
    code: string
    isRecoveryCode?: boolean
  }): Promise<IRecoveryCodesResponse>
  disable2FA(params: {
    code: string
    isRecoveryCode?: boolean
  }): Promise<{ result: boolean }>
  toggle2FA(enabled: boolean): Promise<{ result: boolean }>
}

export class UserAPIServiceV2 implements IUserAPIServiceV2 {
  /**
   * Firebase 인증으로 로그인 (V2)
   * V1: POST /user/login (세션 기반)
   * V2: POST /user/login (JWT + Firebase)
   */
  async login(): Promise<ILoginResponseV2> {
    const auth = getFirebaseAuth()
    if (!auth || !auth.currentUser) {
      throw new Error('Firebase authentication required')
    }

    // Firebase ID 토큰 가져오기
    const idToken = await auth.currentUser.getIdToken()

    const response = await apiInterceptor.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    })

    if (!response.ok) {
      throw new Error(`Login failed: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조: { success, data: { accessToken, refreshToken, user, twoFactorEnabled, walletId, ... } }
    const responseData = data.data || data

    // JWT 토큰 저장
    const accessToken = responseData.accessToken
    const refreshToken = responseData.refreshToken

    if (accessToken && refreshToken) {
      apiInterceptor.saveTokens(accessToken, refreshToken)
    }

    // 사용자 ID 저장
    if (responseData.user?.id) {
      localStorage.setItem('userId', responseData.user.id)
    }

    // walletId 저장 (기본 지갑)
    if (responseData.walletId) {
      localStorage.setItem('defaultWalletId', responseData.walletId)
    }

    // 2FA 상태 저장
    if (responseData.twoFactorEnabled !== undefined) {
      localStorage.setItem(
        'twoFactorEnabled',
        String(responseData.twoFactorEnabled),
      )
    }

    return data
  }

  /**
   * 로그아웃 (V2)
   * V1: POST /user/logout
   * V2: POST /user/logout
   */
  async logout(): Promise<void> {
    const response = await apiInterceptor.request('/user/logout', {
      method: 'POST',
    })

    if (!response.ok) {
      // Logout API call failed - continue anyway
    }

    // Firebase 로그아웃
    const auth = getFirebaseAuth()
    if (auth) {
      await auth.signOut()
    }

    // 토큰 제거
    apiInterceptor.logout()
  }

  /**
   * 사용자 정보 조회 (V2)
   * V1: GET /user/detail
   * V2: GET /user/me
   */
  async getUserDetail(): Promise<IUserV2> {
    const response = await apiInterceptor.request('/user/me')
    if (!response.ok) {
      throw new Error(`Failed to fetch user detail: ${response.statusText}`)
    }

    const data = await response.json()
    // V2 API 응답 구조: { success, data: { user, providers, settings } }
    // user 객체에서 사용자 정보 추출
    const userData = data.data?.user || data.user || data.data || data
    // settings에서 twoFactorEnabled 추출하여 user에 포함
    const settings = data.data?.settings
    if (settings?.twoFactorEnabled !== undefined) {
      userData.is2FAEnabled = settings.twoFactorEnabled
    }
    return userData
  }

  /**
   * 사용자 정보 업데이트 (V2)
   * V1: PATCH /user/update
   * V2: PATCH /user/me
   */
  async updateUser(updates: Partial<IUserV2>): Promise<IUserV2> {
    const response = await apiInterceptor.request('/user/me', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      throw new Error(`Failed to update user: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data || data.user || data
  }

  /**
   * 프로필 이미지 업로드 (V2)
   * POST /user/me/profile-image
   * FormData로 파일 업로드 후 profileImageUrl 반환
   */
  async uploadProfileImage(
    formData: FormData,
  ): Promise<{ profileImageUrl: string }> {
    const response = await apiInterceptor.request('/user/me/profile-image', {
      method: 'POST',
      body: formData,
      // FormData 전송 시 Content-Type 헤더를 설정하지 않음 (브라우저가 자동 설정)
    })

    if (!response.ok) {
      throw new Error(`Failed to upload profile image: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data || data
  }

  /**
   * 2FA 설정 시작 (V2)
   * POST /user/me/2fa
   * QR코드, 시크릿, 복구코드 생성
   */
  async setup2FA(): Promise<I2FASetupResponse> {
    const response = await apiInterceptor.request('/user/me/2fa', {
      method: 'POST',
    })

    if (!response.ok) {
      throw new Error(`Failed to setup 2FA: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data || data
  }

  /**
   * OTP/복구코드 검증 (V2)
   * POST /user/me/2fa/verify
   */
  async verify2FA(params: {
    code: string
    isRecoveryCode?: boolean
  }): Promise<I2FAVerifyResponse> {
    const response = await apiInterceptor.request('/user/me/2fa/verify', {
      method: 'POST',
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      throw new Error(`Failed to verify 2FA: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data || data
  }

  /**
   * 2FA 상태 조회 (V2)
   * GET /user/me/2fa/status
   */
  async get2FAStatus(): Promise<I2FAStatusResponse> {
    const response = await apiInterceptor.request('/user/me/2fa/status')

    if (!response.ok) {
      throw new Error(`Failed to get 2FA status: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data || data
  }

  /**
   * 복구 코드 조회 (V2)
   * GET /user/me/2fa/recovery-codes
   * OTP 인증 필요 - query param으로 code 전달
   */
  async getRecoveryCodes(params: {
    code: string
    isRecoveryCode?: boolean
  }): Promise<IRecoveryCodesResponse> {
    const queryParams = new URLSearchParams()
    queryParams.append('code', params.code)
    if (params.isRecoveryCode) {
      queryParams.append('isRecoveryCode', 'true')
    }

    const response = await apiInterceptor.request(
      `/user/me/2fa/recovery-codes?${queryParams.toString()}`,
    )

    if (!response.ok) {
      throw new Error(`Failed to get recovery codes: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data || data
  }

  /**
   * 복구 코드 재생성 (V2)
   * PATCH /user/me/2fa/recovery-codes
   */
  async regenerateRecoveryCodes(params: {
    code: string
    isRecoveryCode?: boolean
  }): Promise<IRecoveryCodesResponse> {
    const response = await apiInterceptor.request(
      '/user/me/2fa/recovery-codes',
      {
        method: 'PATCH',
        body: JSON.stringify(params),
      },
    )

    if (!response.ok) {
      throw new Error(
        `Failed to regenerate recovery codes: ${response.statusText}`,
      )
    }

    const data = await response.json()
    return data.data || data
  }

  /**
   * 2FA 비활성화 (V2)
   * DELETE /user/me/2fa
   * V2 응답: { disabled: true } → { result: true } 로 변환
   */
  async disable2FA(params: {
    code: string
    isRecoveryCode?: boolean
  }): Promise<{ result: boolean }> {
    const response = await apiInterceptor.request('/user/me/2fa', {
      method: 'DELETE',
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      throw new Error(`Failed to disable 2FA: ${response.statusText}`)
    }

    const data = await response.json()
    const responseData = data.data || data

    // V2 응답 { disabled: true } → V1 형식 { result: true } 로 변환
    return { result: responseData.disabled === true }
  }

  /**
   * 2FA 토글 (V2)
   * 활성화/비활성화 상태 변경
   */
  async toggle2FA(_enabled: boolean): Promise<{ result: boolean }> {
    // 2FA 토글은 V2 API에서 지원하지 않음 - 활성화는 setup + verify, 비활성화는 disable2FA로 처리
    // 이 메서드는 호환성을 위해 존재하며, 실제 구현은 호출하는 곳에서 처리
    return { result: true }
  }

  /**
   * 계정 삭제 (V2)
   * V1: DELETE /user
   * V2: DELETE /user/me
   */
  async deleteUser(): Promise<void> {
    const response = await apiInterceptor.request('/user/me', {
      method: 'DELETE',
    })

    if (!response.ok) {
      throw new Error(`Failed to delete user: ${response.statusText}`)
    }

    // Firebase 계정 삭제
    const auth = getFirebaseAuth()
    if (auth && auth.currentUser) {
      await auth.currentUser.delete()
    }

    // 로그아웃 처리
    apiInterceptor.logout()
  }

  /**
   * 액세스 토큰 갱신 (V2)
   * V1: 없음 (세션 기반)
   * V2: GET /user/access-token
   */
  async refreshAccessToken(refreshToken: string): Promise<string> {
    const response = await fetch(
      `${import.meta.env.VITE_WEPIN_BACKEND_V2_URL}/user/access-token`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
      },
    )

    if (!response.ok) {
      throw new Error(`Failed to refresh token: ${response.statusText}`)
    }

    const data = await response.json()
    return data.accessToken
  }

  /**
   * Enclave 공개키 조회 (V2)
   * V2: GET /auth/publickey
   * 응답: { success: true, data: { status, b64PubKey }, message }
   */
  async getPubKey(): Promise<{ pubKey: string }> {
    const response = await apiInterceptor.request('/auth/publickey')

    if (!response.ok) {
      throw new Error(`Failed to get public key: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조: { success: true, data: { b64PubKey: '...' } }
    if (data.success && data.data?.b64PubKey) {
      return { pubKey: data.data.b64PubKey }
    }

    // Fallback: data에 직접 있는 경우
    if (data.data?.pubKey) {
      return { pubKey: data.data.pubKey }
    }

    throw new Error('Invalid public key response format')
  }

  /**
   * 이메일 존재 여부 확인 (V2)
   * V1: GET /user/exist
   * V2: GET /user/exist?email=xxx
   */
  async checkEmailExist(email: string): Promise<ICheckEmailExistResponse> {
    const response = await apiInterceptor.request(
      `/user/exist?email=${encodeURIComponent(email)}`,
    )

    if (!response.ok) {
      throw new Error(`Failed to check email: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답: { success, data: { isEmailExist, isEmailverified, providerIds } }
    const result = data.data || data
    return {
      exists: result.isEmailExist || false,
      isEmailExist: result.isEmailExist || false,
      isEmailverified: result.isEmailverified || false,
      providerIds: result.providerIds || [],
    }
  }

  /**
   * 사용자 검증 (V2)
   * V1: POST /user/verify
   * V2: POST /user/verify
   */
  async verifyUser(params: {
    type: string
    email: string
    localeId?: number
    promotionId?: string
    campaignId?: string
    referralCode?: string
  }): Promise<any> {
    const response = await apiInterceptor.request('/user/verify', {
      method: 'POST',
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      throw new Error(`Failed to verify user: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data || data
  }

  /**
   * 이메일로 패스워드 상태 조회 (V2)
   * GET /user/password-state?email=xxx
   */
  async getPasswordStateByEmail(
    email: string,
  ): Promise<{ isPasswordResetRequired: boolean }> {
    const response = await apiInterceptor.request(
      `/user/password-state?email=${encodeURIComponent(email)}`,
    )

    if (!response.ok) {
      // 에러 시 기본값 반환 (암호화 완료 상태)
      return { isPasswordResetRequired: false }
    }

    const data = await response.json()
    const result = data.data || data
    return {
      isPasswordResetRequired: result.isPasswordResetRequired ?? false,
    }
  }

  /**
   * App 목록 조회 (V2)
   * V2: GET /apps
   */
  async getApps(): Promise<IApp[]> {
    const response = await apiInterceptor.request('/apps')

    if (!response.ok) {
      throw new Error(`Failed to fetch apps: ${response.statusText}`)
    }

    const data = await response.json()

    // 응답 구조에 따라 적절히 파싱
    return data.apps || data.data || data
  }

  /**
   * 사용자가 참여한 App 목록 조회 (V2)
   * V2: GET /apps/me
   * 응답: { success: true, data: [...], message: "User joined apps retrieved successfully" }
   */
  async getAppsMe(): Promise<IApp[]> {
    const response = await apiInterceptor.request('/apps/me')

    if (!response.ok) {
      throw new Error(`Failed to fetch user apps: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조: { success: true, data: [...] }
    if (data.success && Array.isArray(data.data)) {
      return data.data
    }

    // Fallback
    return data.apps || data.data || data
  }

  /**
   * OAuth Provider 목록 조회 (V2)
   * V2: GET /oauth/oauth-provider
   */
  async getOAuthProviders(): Promise<IOAuthProvider[]> {
    const response = await apiInterceptor.request('/oauth/oauth-provider')

    if (!response.ok) {
      throw new Error(`Failed to fetch OAuth providers: ${response.statusText}`)
    }

    const data = await response.json()
    // 응답 구조: { success, message, data: { providers: [...] } }
    return data.data?.providers || data.data || data
  }
}

// 싱글톤 인스턴스
export const userAPIServiceV2 = new UserAPIServiceV2()
